import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2, RotateCcw, Trash2, MessageSquare, Bug, Lightbulb, HelpCircle, MoreHorizontal, LifeBuoy, Paperclip, FileDown, Upload, X } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const STATUS_META = {
  aperta:         { label: "Aperta",         cls: "bg-amber-50 text-amber-800 border-amber-200" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  risolta:        { label: "Risolta",        cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  chiusa:         { label: "Chiusa",         cls: "bg-stone-100 text-stone-700 border-stone-200" },
};
const PRIORITY_META = {
  bassa:   { label: "Bassa",   cls: "bg-stone-100 text-stone-700 border-stone-200" },
  media:   { label: "Media",   cls: "bg-blue-50 text-blue-800 border-blue-200" },
  alta:    { label: "Alta",    cls: "bg-orange-50 text-orange-800 border-orange-200" },
  urgente: { label: "Urgente", cls: "bg-red-50 text-red-800 border-red-200" },
};
const CATEGORY_META = {
  bug:      { label: "Bug",         icon: Bug,         color: "#dc2626" },
  feature:  { label: "Suggerimento",icon: Lightbulb,   color: "#7c3aed" },
  question: { label: "Domanda",     icon: HelpCircle,  color: "#0369a1" },
  other:    { label: "Altro",       icon: MoreHorizontal, color: "#78716c" },
};

export default function HelpDeskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = ["admin", "direzione", "segreteria"].includes(user?.role);
  const canAssign = ["admin", "direzione"].includes(user?.role);
  const { data: assignees = [] } = useQuery({
    queryKey: ["helpdesk-assignees"],
    queryFn: async () => (await api.get("/helpdesk/assignees")).data,
    enabled: canAssign,
  });

  const [newComment, setNewComment] = useState("");
  const [resolution, setResolution] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: t, isLoading } = useQuery({
    queryKey: ["helpdesk-ticket", id],
    queryFn: async () => (await api.get(`/helpdesk/tickets/${id}`)).data,
  });

  const addComment = useMutation({
    mutationFn: async () => (await api.post(`/helpdesk/tickets/${id}/comments`, { body: newComment.trim() })).data,
    onSuccess: () => {
      setNewComment("");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const updateMut = useMutation({
    mutationFn: async (payload) => (await api.patch(`/helpdesk/tickets/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Segnalazione aggiornata");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const closeMut = useMutation({
    mutationFn: async () => (await api.post(`/helpdesk/tickets/${id}/close`, null, { params: resolution ? { resolution } : {} })).data,
    onSuccess: () => {
      toast.success("Segnalazione chiusa");
      setResolution("");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const reopenMut = useMutation({
    mutationFn: async () => (await api.post(`/helpdesk/tickets/${id}/reopen`)).data,
    onSuccess: () => {
      toast.success("Segnalazione riaperta");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const archiveMut = useMutation({
    mutationFn: async (archived) => (await api.post(`/helpdesk/tickets/${id}/archive`, null, { params: { archived } })).data,
    onSuccess: (_, archived) => {
      toast.success(archived ? "Segnalazione archiviata" : "Segnalazione ripristinata");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/helpdesk/tickets/${id}`)).data,
    onSuccess: () => {
      toast.success("Segnalazione eliminata");
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      nav("/helpdesk");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (isLoading || !t) return <div className="p-10 text-sm text-stone-400">Caricamento…</div>;

  const CatIcon = CATEGORY_META[t.category]?.icon || HelpCircle;
  const catColor = CATEGORY_META[t.category]?.color || "#78716c";
  const isClosed = t.status === "chiusa" || t.status === "risolta";
  const comments = t.comments || [];
  const attachments = t.attachments || [];

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/helpdesk/tickets/${id}/attachments`, fd);
      toast.success("Allegato caricato");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  }

  async function deleteAttachment(att) {
    if (!window.confirm(`Eliminare l'allegato "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/helpdesk/attachments/${att.id}`);
      toast.success("Allegato eliminato");
      qc.invalidateQueries({ queryKey: ["helpdesk-ticket", id] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  function openAttachment(att) {
    const token = localStorage.getItem("token");
    const base = process.env.REACT_APP_BACKEND_URL;
    openFile(`${base}/api/helpdesk/attachments/${att.id}/download?auth=${encodeURIComponent(token)}`, att.filename || att.original_filename);
  }

  function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="helpdesk-detail-page">
      <Link to="/helpdesk" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna all'elenco
      </Link>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: `${catColor}20`, color: catColor }}>
          <CatIcon size={20}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            #{t.id.slice(0, 8).toUpperCase()} · {CATEGORY_META[t.category]?.label}
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1C352D] mt-0.5">{t.title}</h1>
          <div className="text-[12px] text-stone-500 mt-1">
            Aperta da <b className="text-stone-700">{t.created_by_name}</b> · {fmtDate(t.created_at)}
            {t.closed_at && <> · chiusa {fmtDate(t.closed_at)}</>}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge className={`text-[11px] rounded-full border ${STATUS_META[t.status]?.cls}`}>{STATUS_META[t.status]?.label}</Badge>
            <Badge className={`text-[11px] rounded-full border ${PRIORITY_META[t.priority]?.cls}`}>Priorità {PRIORITY_META[t.priority]?.label}</Badge>
            {t.assigned_to_name && <Badge className="text-[11px] rounded-full border bg-stone-50 text-stone-700 border-stone-200">Assegnata a {t.assigned_to_name}</Badge>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Thread */}
        <div className="space-y-4">
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Descrizione</div>
            <div className="whitespace-pre-wrap text-sm text-stone-800">{t.description}</div>
          </Card>

          <Card className="border border-stone-200 rounded-xl shadow-none">
            <div className="p-4 border-b border-stone-100 text-sm font-medium flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2"><Paperclip size={14}/> Allegati <span className="text-stone-400 font-normal">· {attachments.length}</span></span>
              {!isClosed && (
                <label className={`cursor-pointer inline-flex items-center gap-2 rounded-full border border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white px-3 h-8 text-[12px] transition-colors duration-150 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                  <Upload size={13}/> {uploading ? "Caricamento…" : "Allega file"}
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.txt,.log,.csv,.zip" onChange={onUpload} disabled={uploading} data-testid="ticket-attach-input"/>
                </label>
              )}
            </div>
            {attachments.length === 0 ? (
              <div className="p-5 text-xs text-stone-400 text-center">Nessun allegato. Puoi aggiungere screenshot o file per spiegare meglio il problema (max 15 MB).</div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {attachments.map((att) => {
                  const canDelete = isManager || att.uploaded_by === user?.id;
                  return (
                    <li key={att.id} className="p-3 flex items-center gap-3" data-testid={`attachment-${att.id}`}>
                      <div className="w-9 h-9 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0 text-stone-500 text-[10px] font-medium uppercase">
                        .{att.extension}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate text-stone-800">{att.original_filename}</div>
                        <div className="text-[11px] text-stone-500 truncate">{formatSize(att.size)} · caricato da {att.uploaded_by_name} · {fmtDate(att.uploaded_at)}</div>
                      </div>
                      <button onClick={() => openAttachment(att)} className="text-stone-500 hover:text-[#1C352D] p-2 rounded-full hover:bg-stone-50 transition-colors duration-150" title="Apri">
                        <FileDown size={15}/>
                      </button>
                      {canDelete && !isClosed && (
                        <button onClick={() => deleteAttachment(att)} className="text-stone-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors duration-150" title="Elimina">
                          <X size={15}/>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="border border-stone-200 rounded-xl shadow-none">
            <div className="p-4 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
              <MessageSquare size={14}/> Conversazione <span className="text-stone-400 font-normal">· {comments.length}</span>
            </div>
            {comments.length === 0 ? (
              <div className="p-6 text-sm text-stone-400 text-center">Nessun commento ancora. Scrivi il primo qui sotto.</div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {comments.map((c) => (
                  <li key={c.id} className="p-4" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1 text-[11px] text-stone-500">
                      <b className="text-stone-800">{c.author_name}</b>
                      <span>· {c.author_role}</span>
                      <span>· {fmtDate(c.created_at)}</span>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-stone-800">{c.body}</div>
                  </li>
                ))}
              </ul>
            )}

            {!isClosed && (
              <div className="p-4 border-t border-stone-100 space-y-2">
                <Textarea rows={3} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Aggiungi un commento o un aggiornamento…" data-testid="new-comment-input"/>
                <div className="flex justify-end">
                  <Button
                    onClick={() => addComment.mutate()}
                    disabled={!newComment.trim() || addComment.isPending}
                    className="bg-[#1C352D] hover:bg-[#264A3E]"
                    data-testid="send-comment-btn"
                  >
                    <Send size={13} className="mr-1.5"/> Invia
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {t.resolution && (
            <Card className="p-5 border border-emerald-200 bg-emerald-50/40 rounded-xl shadow-none">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Risoluzione</div>
              <div className="whitespace-pre-wrap text-sm text-stone-800">{t.resolution}</div>
            </Card>
          )}
        </div>

        {/* Sidebar azioni */}
        <div className="space-y-4">
          {isManager && (
            <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Gestione</div>
              <div>
                <label className="text-[11px] text-stone-500">Assegnata a</label>
                {canAssign ? (
                  <Select value={t.assigned_to || "__none"} onValueChange={(v) => updateMut.mutate({ assigned_to: v === "__none" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="change-assignee"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Nessuno</SelectItem>
                      {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email} · {a.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1 text-sm font-medium" data-testid="assignee-readonly">{t.assigned_to_name || "Non assegnata"}</div>
                )}
                {t.assigned_by_name && t.assigned_to && <div className="text-[10px] text-stone-400 mt-1">da {t.assigned_by_name}{t.assigned_at ? ` · ${fmtDate(t.assigned_at)}` : ""}</div>}
              </div>
              <div>
                <label className="text-[11px] text-stone-500">Stato</label>
                <Select value={t.status} onValueChange={(v) => updateMut.mutate({ status: v })}>
                  <SelectTrigger className="mt-1" data-testid="change-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-stone-500">Priorità</label>
                <Select value={t.priority} onValueChange={(v) => updateMut.mutate({ priority: v })}>
                  <SelectTrigger className="mt-1" data-testid="change-priority"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-stone-500">Tipologia</label>
                <Select value={t.category} onValueChange={(v) => updateMut.mutate({ category: v })}>
                  <SelectTrigger className="mt-1" data-testid="change-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          )}

          {!isClosed && (
            <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Chiudi segnalazione</div>
              <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Note di chiusura (opzionale)" data-testid="close-resolution"/>
              <Button onClick={() => closeMut.mutate()} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="close-ticket-btn">
                <CheckCircle2 size={14} className="mr-1.5"/> Chiudi segnalazione
              </Button>
            </Card>
          )}

          {isClosed && isManager && (
            <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-2">
              <Button onClick={() => archiveMut.mutate(!t.archived)} className="w-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="archive-ticket-btn">
                {t.archived ? "Ripristina dall'archivio" : "Archivia segnalazione"}
              </Button>
              <Button variant="outline" onClick={() => reopenMut.mutate()} className="w-full" data-testid="reopen-ticket-btn">
                <RotateCcw size={14} className="mr-1.5"/> Riapri segnalazione
              </Button>
            </Card>
          )}

          {["admin", "direzione"].includes(user?.role) && (
            <Card className="p-4 border border-red-200 rounded-xl shadow-none">
              <Button
                variant="outline"
                onClick={() => { if (window.confirm("Eliminare definitivamente la segnalazione?")) deleteMut.mutate(); }}
                className="w-full text-red-700 border-red-200 hover:bg-red-50"
                data-testid="delete-ticket-btn"
              >
                <Trash2 size={14} className="mr-1.5"/> Elimina segnalazione
              </Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
